import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { settingsApi } from '../services/api/settings.api';
import MaintenancePage from '../components/pages/MaintenancePage';
import MaintenanceAlert from '../components/common/MaintenanceAlert';
import { usePermissions } from '../contexts/PermissionsContext';

// Leader-election & broadcast constants
const LEADER_KEY = 'maintenance_leader';
const DATA_KEY   = 'maintenance_data';
const LEADER_TTL = 10000;  // 10s
const POLL_MS    = 60000;  // 1 min

const MaintenanceMiddleware = ({ children }) => {
    const [isLoading, setIsLoading] = useState(true);
    const [maintenanceData, setMaintenanceData] = useState(null);
    const location = useLocation();
    const { hasPermission } = usePermissions();

    // التحقق إذا كان المستخدم في لوحة التحكم أو صفحة تسجيل الدخول
    const isInDashboard = location.pathname.startsWith('/admin/dashboard');
    const isInLogin = location.pathname.startsWith('/admin/login');

    const checkMaintenanceStatus = async () => {
        try {
            const response = await settingsApi.getMaintenanceSettings();
            setMaintenanceData(response);
        } catch (error) {
            console.error('Error checking maintenance status:', error);
        } finally {
            setIsLoading(false);
        }
    };

    // Leader-based polling + localStorage broadcast
    useEffect(() => {
        if (isInDashboard || isInLogin) {
            setIsLoading(false);
            return; // Skip middleware inside dashboard / login
        }

        const syncFromLS = () => {
            try {
                const raw = localStorage.getItem(DATA_KEY);
                if (raw) {
                    const data = JSON.parse(raw);
                    setMaintenanceData(data);
                    setIsLoading(false);
                }
            } catch (_) {}
        };

        const becomeLeader = () => {
            const now = Date.now();
            const last = parseInt(localStorage.getItem(LEADER_KEY) || '0', 10);
            if (isNaN(last) || now - last > LEADER_TTL) {
                localStorage.setItem(LEADER_KEY, String(now));
                return true;
            }
            return false;
        };

        const fetchStatus = async () => {
            try {
                const res = await settingsApi.getMaintenanceSettings();
                setMaintenanceData(res);
                localStorage.setItem(DATA_KEY, JSON.stringify(res));
            } catch (error) {
                console.error('Error checking maintenance status:', error);
            } finally {
                setIsLoading(false);
            }
        };

        // Initial sync from LS for fast paint
        syncFromLS();

        let intervalId;
        if (becomeLeader()) {
            // I'm the leader → start polling
            fetchStatus();
            intervalId = setInterval(fetchStatus, POLL_MS);
        }

        // Listen for broadcasts and visibility changes
        const onStorage = (e) => {
            if (e.key === DATA_KEY && e.newValue) {
                syncFromLS();
            }
        };
        window.addEventListener('storage', onStorage);
        document.addEventListener('visibilitychange', syncFromLS);

        return () => {
            clearInterval(intervalId);
            window.removeEventListener('storage', onStorage);
            document.removeEventListener('visibilitychange', syncFromLS);
        };
    }, [isInDashboard, isInLogin]);

    // أثناء التحميل
    if (isLoading) {
        return null;
    }

    // إذا كان في لوحة التحكم أو صفحة تسجيل الدخول
    if (isInDashboard || isInLogin) {
        return children;
    }

    // إذا كان وضع الصيانة مفعل
    if (maintenanceData && maintenanceData.is_enabled === true) {
        // التحقق من صلاحية مشاهدة الموقع أثناء الصيانة
        const canViewSiteInMaintenance = hasPermission('view_site_in_maintenance');
        
        // إذا كان المستخدم يملك الصلاحية، يمكنه مشاهدة الموقع مع تنبيه
        if (canViewSiteInMaintenance) {
            return (
                <>
                    <MaintenanceAlert data={maintenanceData} />
                    {children}
                </>
            );
        }
        
        // إذا لم يملك الصلاحية، يتم توجيهه لصفحة الصيانة
        return <MaintenancePage data={maintenanceData} />;
    }

    // الحالة العادية
    return children;
};

export default MaintenanceMiddleware;
